import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';
import { Message } from '@/types';
import { saveMessageService, updateLastMessageService } from './services';
import { saveGeneralMessageRPC } from './services/generalChatMessageService';
import { generateDocumentSummary } from '@/utils/api';
import { generateContextualSuggestions, DocumentContext } from './contextualAnalysisService';
import { documentContextManager } from './documentContextManager';

/**
 * Procura por um resumo existente no database para o texto extraído
 */
async function findExistingDocumentSummary(conversationId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('document_extractions')
      .select('summary, extracted_text')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (error) {
      console.error('Erro ao buscar resumo existente:', error);
      return null;
    }
    
    if (!data || !data.summary) {
      console.log('Nenhum resumo existente encontrado');
      return null;
    }
    
    if (data.summary.includes('Ocorreu um erro ao processar sua solicitação') && data.summary.length < 100) {
      console.log('Resumo existente é uma mensagem de erro, ignorando.');
      return null;
    }
    
    console.log('Using existing summary from database');
    console.log('Extracted text length in database:', data.extracted_text?.length || 0);
    return data.summary;
  } catch (error) {
    console.error('Error finding existing summary:', error);
    return null;
  }
}

/**
 * Generates a summary for an uploaded document with contextual analysis
 */
export async function generateSummaryForDocument(
  extractedText: string, 
  conversationId: string,
  messages: Message[],
  userId: string,
  requestId: string,
  originUserMessageId: string
): Promise<Message | null> {
  try {
    console.log("📄 Analyzing document with contextual intelligence...");
    console.log("Extracted text length:", extractedText.length);
    
    if (!extractedText || extractedText.length < 50) {
      console.log("Texto extraído muito curto ou inválido:", extractedText?.substring(0, 100));
      return createFallbackSummaryMessage(extractedText, conversationId, userId, requestId, originUserMessageId);
    }
    
    console.log("📝 Processing document with", extractedText.length, "characters");
    
    // Store extracted text
    try {
      console.log("💾 Saving extracted text to database");
      const { error: storeError } = await supabase
        .from('document_extractions')
        .upsert({
          conversation_id: conversationId,
          user_id: userId,
          extracted_text: extractedText,
          created_at: new Date().toISOString()
        });
      
      if (storeError) {
        console.error("Error storing extracted text:", storeError);
      } else {
        console.log("✅ Extracted text stored successfully");
      }
    } catch (storeError) {
      console.error("Exception storing extracted text:", storeError);
    }
    
    // Check for existing summary
    console.log("🔍 Checking for existing document summary");
    const existingSummary = await findExistingDocumentSummary(conversationId);
    
    let summary: string;
    if (existingSummary && existingSummary.length > 100) {
      summary = existingSummary;
      console.log("♻️ Using existing summary from database");
    } else {
      // Generate new summary with 10-line limit
      console.log("🤖 Generating new contextual summary...");
      
      try {
        console.log("Preview of extracted text:", extractedText.substring(0, 500) + "...");
        
        // Create enhanced prompt for contextual summary (max 10 lines)
        const contextualSummaryPrompt = `
Analise este documento jurídico e crie um resumo executivo de MÁXIMO 10 LINHAS.

DOCUMENTO A ANALISAR:
${extractedText}

INSTRUÇÕES:
1. Máximo 10 linhas de resumo
2. Identifique: tipo de documento, partes envolvidas, valores, datas importantes
3. Seja objetivo e específico
4. Use dados reais extraídos do documento
5. Format: • Tipo: [tipo] • Partes: [nomes] • Valor: [valor] • Data: [data] etc.

IMPORTANTE: Responda APENAS com o resumo de 10 linhas, sem introduções ou explicações.`;

        const { data, error } = await supabase.functions.invoke('openai-chat', {
          body: {
            messages: [
              {
                role: 'system',
                content: 'Assistente jurídico especializado. Resumos executivos concisos de máximo 10 linhas. Sem introduções.'
              },
              {
                role: 'user',
                content: contextualSummaryPrompt
              }
            ],
            model: 'gpt-4.1-2025-04-14',
            auto_summarize: false
          }
        });

        if (error) {
          console.error('Error generating summary:', error);
          summary = generateFallbackSummary(extractedText);
        } else {
          summary = data?.content || data?.response || generateFallbackSummary(extractedText);
          
          // Ensure summary is limited to 10 lines
          const summaryLines = summary.split('\n').slice(0, 10);
          summary = summaryLines.join('\n');
          
          console.log("✅ Contextual summary generated successfully, lines:", summaryLines.length);
        }
        
        // Save the generated summary
        try {
          const { error: updateError } = await supabase
            .from('document_extractions')
            .update({ summary })
            .eq('conversation_id', conversationId);
          
          if (updateError) {
            console.error("Error saving summary:", updateError);
          } else {
            console.log("💾 Summary saved successfully");
          }
        } catch (updateError) {
          console.error("Exception saving summary:", updateError);
        }
      } catch (summaryError) {
        console.error("Error generating summary via API:", summaryError);
        summary = generateFallbackSummary(extractedText);
      }
    }
    
    if (!summary || summary.includes('Ocorreu um erro ao processar sua solicitação')) {
      console.log("Failed to generate adequate summary. Using fallback.");
      summary = generateFallbackSummary(extractedText);
    }
    
    // Ensure summary is limited to 10 lines
    const summaryLines = summary.split('\n').slice(0, 10);
    const finalSummary = summaryLines.join('\n');
    
    console.log("📊 Final summary generated:", finalSummary.substring(0, 100) + "...");
    
    // Generate contextual suggestions based on the summary and extracted content
    console.log("💡 Generating contextual suggestions...");
    const documentType = detectDocumentType(extractedText);
    
    // Create the enhanced summary message with contextual information
    const summaryMessage: Message = {
      id: uuidv4(),
      conversation_id: conversationId,
      role: "assistant",
      content: `**📄 Análise Contextual do Documento**

${finalSummary}

---

**💡 Baseado na análise, posso ajudar você com perguntas específicas sobre este documento. Veja as sugestões abaixo:**`,
      created_at: new Date().toISOString(),
    };
    
    // ✅ CORREÇÃO 2: Save summary message IMMEDIATELY to database for fast UI update
    console.log("💾 Saving contextual summary to database...");
    await saveGeneralMessageRPC({
      conversationId,
      role: 'assistant',
      content: summaryMessage.content,
      fileId: null,
      requestId,
      originUserMessageId,
    });
    console.log("✅ Contextual summary saved successfully");
    
    // Generate contextual suggestions (can happen after message is saved)
    const contextualSuggestions = await generateContextualSuggestions(
      finalSummary, 
      documentType, 
      extractedText
    );
    
    console.log(`🎯 Generated ${contextualSuggestions.length} contextual suggestions`);
    
    // Store document context for future reference
    const documentContext: DocumentContext = {
      documentType,
      summary: finalSummary,
      keyData: {
        names: extractNames(extractedText),
        values: extractValues(extractedText),
        dates: extractDates(extractedText),
        processNumbers: extractProcessNumbers(extractedText),
        parties: extractParties(extractedText)
      }
    };
    
    // Save to database for persistence (after message is already visible)
    await documentContextManager.setDocumentContext(
      conversationId,
      documentContext,
      finalSummary,
      contextualSuggestions,
      userId
    );
    
    // Update conversation with last message
    await updateLastMessageService(conversationId, summaryMessage.content, userId);
    
    // Return immediately for React state update
    return summaryMessage;
  } catch (summaryError) {
    console.error("Error generating contextual document summary:", summaryError);
    return createFallbackSummaryMessage(extractedText, conversationId, userId, requestId, originUserMessageId);
  }
}

// Helper functions to extract specific data types
function extractNames(text: string): string[] {
  const lines = text.split('\n').map(line => line.trim());
  const names: string[] = [];
  
  for (const line of lines) {
    if (line.match(/^[A-ZÁÊÇÕ][a-záêçõ]+(?:\s+[A-ZÁÊÇÕ][a-záêçõ]+)+$/)) {
      if (!line.includes('@') && !line.match(/\d/) && line.length > 5 && line.length < 50) {
        names.push(line);
      }
    }
    
    const nameMatch = line.match(/(?:Nome|NOME):\s*([A-ZÁÊÇÕ][a-záêçõ\s]+)/);
    if (nameMatch) {
      names.push(nameMatch[1].trim());
    }
  }
  
  return [...new Set(names)].slice(0, 5);
}

function extractValues(text: string): string[] {
  const valueMatches = text.match(/R\$\s*[\d.,]+/gi) || [];
  return [...new Set(valueMatches)].slice(0, 5);
}

function extractDates(text: string): string[] {
  const dateMatches = text.match(/\d{1,2}\/\d{1,2}\/\d{4}/g) || [];
  return [...new Set(dateMatches)].slice(0, 5);
}

function extractProcessNumbers(text: string): string[] {
  const processMatches = text.match(/\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}/g) || [];
  return [...new Set(processMatches)];
}

function extractParties(text: string): string[] {
  const parties: string[] = [];
  const reclamanteMatch = text.match(/reclamante:?\s*([^,\n\.]+)/i);
  const reclamadoMatch = text.match(/reclamad[oa]:?\s*([^,\n\.]+)/i);
  const autorMatch = text.match(/autor:?\s*([^,\n\.]+)/i);
  const reuMatch = text.match(/r[eé]u:?\s*([^,\n\.]+)/i);
  
  if (reclamanteMatch) parties.push(`Reclamante: ${reclamanteMatch[1].trim()}`);
  if (reclamadoMatch) parties.push(`Reclamado: ${reclamadoMatch[1].trim()}`);
  if (autorMatch) parties.push(`Autor: ${autorMatch[1].trim()}`);
  if (reuMatch) parties.push(`Réu: ${reuMatch[1].trim()}`);
  
  return parties;
}

function detectDocumentType(text: string): string {
  const upperText = text.toUpperCase();
  
  if (upperText.includes('RECLAMAÇÃO TRABALHISTA') || upperText.includes('TST') || upperText.includes('CLT')) {
    return 'PROCESSO_TRABALHISTA';
  } else if (upperText.includes('PETIÇÃO INICIAL') || upperText.includes('PROCESSO CIVIL')) {
    return 'PROCESSO_CIVIL';
  } else if (upperText.includes('CONTRATO')) {
    return 'CONTRATO';
  } else if (upperText.includes('PROCURAÇÃO')) {
    return 'PROCURAÇÃO';
  } else if (upperText.includes('CARTEIRA DE IDENTIDADE') || upperText.includes('CPF')) {
    return 'DOCUMENTO_IDENTIDADE';
  }
  
  return 'DOCUMENTO_JURIDICO';
}

/**
 * Creates a fallback summary message when API fails
 */
async function createFallbackSummaryMessage(
  extractedText: string, 
  conversationId: string, 
  userId: string,
  requestId: string,
  originUserMessageId: string
): Promise<Message> {
  const fallbackSummary = generateFallbackSummary(extractedText);
  
  const fallbackMessage: Message = {
    id: uuidv4(),
    conversation_id: conversationId,
    role: "assistant",
    content: `**📄 Análise do Documento**\n\n${fallbackSummary}`,
    created_at: new Date().toISOString(),
  };
  
  await saveGeneralMessageRPC({
    conversationId,
    role: 'assistant',
    content: fallbackMessage.content,
    fileId: null,
    requestId,
    originUserMessageId,
  });
  await updateLastMessageService(conversationId, fallbackMessage.content, userId);
  
  return fallbackMessage;
}

/**
 * Generates a fallback summary when the API call fails (limited to 10 lines)
 */
function generateFallbackSummary(extractedText: string): string {
  let processNumber = '';
  const processMatch = extractedText.match(/\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}/);
  if (processMatch) {
    processNumber = processMatch[0];
  }
  
  let reclamante = '';
  let reclamado = '';
  
  const reclamanteMatch = extractedText.match(/reclamante:?\s*([^,\n\.]+)/i);
  if (reclamanteMatch && reclamanteMatch[1]) {
    reclamante = reclamanteMatch[1].trim();
  }
  
  const reclamadoMatch = extractedText.match(/reclamad[oa]:?\s*([^,\n\.]+)/i);
  if (reclamadoMatch && reclamadoMatch[1]) {
    reclamado = reclamadoMatch[1].trim();
  }
  
  let valorCausa = '';
  const valorMatch = extractedText.match(/valor\s*da\s*causa:?\s*r\$\s*([\d.,]+)/i);
  if (valorMatch && valorMatch[1]) {
    valorCausa = `R$ ${valorMatch[1]}`;
  }
  
  // Create a concise 10-line summary
  let summary = `• Tipo: Documento Jurídico - Petição Trabalhista
• Processo: ${processNumber || 'Não identificado'}
• Reclamante: ${reclamante || 'Não identificado'}
• Reclamado: ${reclamado || 'Não identificado'}
• Valor da Causa: ${valorCausa || 'Não informado'}
• Tamanho: ${extractedText.length} caracteres de texto extraído
• Status: Documento processado e analisado
• Tribunal: Justiça do Trabalho (inferido)
• Observação: Análise automática do conteúdo
• Próximos passos: Consultar advogado para orientação específica`;

  return summary;
}
